'use client';

import { useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { Loader2 } from 'lucide-react';
import Link from 'next/link';
import { identifyProductUser, recordProductEvent } from '@/lib/product-analytics';
import { safeReturnPath } from '@/lib/safe-return-path';
import '@/styles/brutalist/forwarder-login.css';

function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isEmailNotConfirmed, setIsEmailNotConfirmed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const searchParams = useSearchParams();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResendSuccess(false);

    const supabase = createClient();

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        recordProductEvent('account_login_failed');

        const isNotConfirmed = signInError.message.toLowerCase().includes('email not confirmed');
        setIsEmailNotConfirmed(isNotConfirmed);
        let friendlyMessage = signInError.message;
        if (signInError.message === 'Invalid login credentials') {
          friendlyMessage = 'The email or password you entered is incorrect. Please try again or reset your password.';
        } else if (signInError.message.toLowerCase().includes('email not confirmed')) {
          friendlyMessage = 'Your email address has not been confirmed yet. Please check your inbox for a confirmation link.';
        } else if (signInError.message.toLowerCase().includes('too many requests')) {
          friendlyMessage = 'Too many login attempts. Please wait a moment and try again.';
        }
        setError(friendlyMessage);
        setIsLoading(false);
        return;
      }

      if (!data.user) {
        setError('Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, company_name')
        .eq('id', data.user.id)
        .maybeSingle<{ role: string; company_name: string | null }>();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        setError(`Profile error: ${profileError.message}. Contact support.`);
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      if (!profile) {
        console.error('No profile found for user:', data.user.id);
        setError('No profile found. Contact support to complete your account setup.');
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      if (profile.role === 'admin') {
        setError('Please use the admin login portal.');
        await supabase.auth.signOut();
        setIsLoading(false);
        return;
      }

      identifyProductUser(data.user.id, profile.role === 'shipper' ? 'shipper' : 'forwarder');
      recordProductEvent('account_login', { account_type: profile.role === 'shipper' ? 'shipper' : 'forwarder' });

      // Role-aware default: shippers go to My Shipments, forwarders to their portal
      const defaultRedirect = profile.role === 'shipper'
        ? '/dashboard/my-shipments'
        : '/forwarders/dashboard';
      const finalRedirect = safeReturnPath(searchParams.get('redirect'), defaultRedirect);

      setTimeout(() => {
        window.location.href = finalRedirect;
      }, 100);
    } catch (err) {
      console.error('[LoginPage] Unexpected login error:', err);
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(`Login failed: ${message}`);
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setIsResending(true);
    setResendSuccess(false);
    setError(null);

    try {
      const response = await fetch('/api/auth/resend-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to resend confirmation email');
        setIsResending(false);
        return;
      }

      recordProductEvent('confirmation_email_resent');

      setResendSuccess(true);
      setIsEmailNotConfirmed(false);
      setIsResending(false);
    } catch {
      setError('Failed to resend confirmation email. Please try again.');
      setIsResending(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-logo">
        <div className="login-logo-text">
          <span style={{ fontSize: '24px' }}>&raquo;</span> Shipzy
        </div>
      </div>

      <div className="login-header">
        <h1 className="login-title">Forwarder Portal</h1>
        <p className="login-description">
          Sign in to access your forwarder dashboard
        </p>
      </div>

      <form onSubmit={handleLogin} className="login-form">
        <div className="login-form-field">
          <label htmlFor="email" className="login-form-label">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@company.com"
            required
            disabled={isLoading}
            autoFocus
            className="login-form-input"
          />
        </div>

        <div className="login-form-field">
          <div className="login-label-row">
            <label htmlFor="password" className="login-form-label">
              Password
            </label>
            <Link href="/forgot-password" className="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={isLoading}
              className="login-form-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#666',
                fontSize: '13px',
              }}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {resendSuccess && (
          <div className="login-success-message-box">
            <p className="login-success-message-text">
              Confirmation email sent! Check your inbox and click the link to confirm your email.
            </p>
          </div>
        )}

        {error && (
          <div className="login-error">
            <p className="login-error-text">{error}</p>
            {isEmailNotConfirmed && (
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={isResending}
                className="login-resend-button"
              >
                {isResending ? (
                  <>
                    <Loader2 className="login-button-icon" style={{ width: 14, height: 14 }} />
                    Resending...
                  </>
                ) : (
                  'Resend Confirmation Email'
                )}
              </button>
            )}
          </div>
        )}

        <button type="submit" className="login-button" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="login-button-icon" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>

        <div className="login-footer">
          Don&apos;t have an account?{' '}
          <Link href="/signup" className="login-footer-link">
            Sign up here
          </Link>
        </div>
      </form>
    </div>
  );
}

export default function ForwarderLoginPage() {
  return (
    <div className="forwarder-login-page">
      <Suspense
        fallback={
          <div className="login-container">
            <div className="login-logo">
              <div className="login-logo-text">
                <span style={{ fontSize: '24px' }}>&raquo;</span> Shipzy
              </div>
            </div>
            <div className="login-success">
              <Loader2 className="login-button-icon" style={{ width: 32, height: 32, margin: '0 auto' }} />
            </div>
          </div>
        }
      >
        <LoginForm />
      </Suspense>
    </div>
  );
}
